import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { Box, Button, Chip, TextField, Typography } from '@mui/material';
import { format } from 'date-fns';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import useUpdateSavTracking from '../../../../../../hooks/orders/useUpdateSavTracking';
import { setSnackBar } from '../../../../../../states/app/staticSlice.js';
import UpdateSavShippingConfirmModal from './UpdateSavShippingConfirmModal.jsx';
import { TrackingNumberButton } from '../../../../../utils/common/TrackingNumberButton.jsx';

const UpdateSavShipping = ({ savDatasFromBO, ticket, profile }) => {
  const dispatch = useDispatch();
  const { updateSavTracking } = useUpdateSavTracking();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [formData, setFormData] = useState({
    tracking: savDatasFromBO?.shipping_items?.[0]?.nr_tracking || '',
  });
  const [originalData] = useState({
    tracking: savDatasFromBO?.shipping_items?.[0]?.nr_tracking || '',
  });

  const handleConfirm = async () => {
    setConfirmLoading(true);
    try {
      if (formData.tracking !== originalData.tracking) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        const data = await updateSavTracking(savDatasFromBO.reference, {
          nr_tracking: formData.tracking,
          reference_hoortrade:
            savDatasFromBO?.shipping_items?.[0]?.reference_hoortrade,
          ticketRef: ticket?.reference,
          author: profile?._id,
        });
        if (!data) {
          throw new Error(
            'Erreur lors de la mise à jour des informations d&apos;expédition'
          );
        }
      }
      const messageError = `Les informations d&apos;expédition ont été mises à jour avec succès`;
      dispatch(
        setSnackBar({ open: true, message: messageError, color: 'success' })
      );
    } catch {
      const messageError = `Erreur lors de la mise à jour des informations d&apos;expédition`;
      dispatch(
        setSnackBar({ open: true, message: messageError, color: 'error' })
      );
    } finally {
      setConfirmLoading(false);
      setIsConfirmOpen(false);
    }
  };

  const getDisabled = () => {
    const hasChanges = formData.tracking !== originalData.tracking;
    if (savDatasFromBO?.is_payable) {
      return savDatasFromBO?.payment_status !== 'payment_waiting' && hasChanges;
      // SAV YUSEN
    } else if (savDatasFromBO?.order_picker === 'STO010003') {
      return savDatasFromBO?.status?.code !== 'VALIDATED' && hasChanges;
      // SAV BUREAU
    } else if (savDatasFromBO?.order_picker === 'STO010002') {
      return savDatasFromBO?.status?.code !== 'IN_PROGRESS' && hasChanges;
    }
    return true;
  };

  return (
    <Box sx={{ mb: 2, mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography
          variant="h6"
          color="primary"
          sx={{ fontWeight: 'bold' }}
          gutterBottom
        >
          Informations d&apos;expédition
        </Typography>
        <TrackingNumberButton
          variant="short"
          shipper={savDatasFromBO?.sender}
          tracking={formData.tracking}
        />
      </Box>

      <Box>
        <Typography variant="body2">
          Date d&apos;expédition :{' '}
          {savDatasFromBO.shipping_date
            ? format(new Date(savDatasFromBO.shipping_date), 'dd/MM/yyyy')
            : 'Non définie'}
        </Typography>
        <Typography variant="body2">
          Expéditeur: {savDatasFromBO?.sender}
        </Typography>
        <Typography variant="body2">
          Statut :{' '}
          <Chip
            label={savDatasFromBO?.status?.name}
            size="small"
            sx={{ ml: 1 }}
          />
        </Typography>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Typography
          color="primary"
          sx={{ fontSize: '1em', fontWeight: 'bold', mb: 1 }}
        >
          Numéro de tracking
        </Typography>
        <TextField
          fullWidth
          value={formData.tracking}
          disabled={savDatasFromBO?.order_picker !== 'STO010002'}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, tracking: e.target.value }))
          }
          placeholder="Entrez le numéro de tracking"
          size="small"
        />
      </Box>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          onClick={() => setIsConfirmOpen(true)}
          disabled={!getDisabled()}
        >
          Mettre à jour
        </Button>
      </Box>

      <UpdateSavShippingConfirmModal
        isOpen={isConfirmOpen}
        handleClose={() => setIsConfirmOpen(false)}
        handleConfirm={() => handleConfirm()}
        formData={formData}
        originalData={originalData}
        confirmLoading={confirmLoading}
      />
    </Box>
  );
};

export default UpdateSavShipping;
